import * as React from 'react';
import { useTranslation } from 'react-i18next';
import * as PropTypes from 'prop-types';
import useClassNames from '@medflex/hooks/useClassNames';
import { trackEvent } from '@medflex/utils/Tracking';
import isMobileDeviceOrApp from '@medflex/utils/isMobileDeviceOrApp';
import { BellIcon } from '@heroicons/react/24/outline';
import Avatar from '../Avatar';
import Brand from '../Brand';
import Button from '../Button';
import {
  IconClose,
  IconCog,
  IconMenu,
  IconQuestion,
  IconStandBy,
  IconUserSolidCircle,
} from '../Icons';
import Link from '../Link';
import ContextMenu from '../ContextMenu';
import Badge from '../Badge/Badge';

const PhysicianTitleBar = ({
  toggleMenu,
  avatar,
  fullNameWithTitle,
  initials,
  className,
  closeIconShown,
  isActive,
  onClickConfig,
  onClickAppSettings,
  onLogout,
  onClickFaq,
  onClickNewFeatures,
  children,
}) => {
  const { t } = useTranslation('components');
  const cn = useClassNames(
    () => ['w-screen h-16 pl-4 pr-5 py-2 border-b flex', className],
    [className],
  );

  return (
    <div data-component="PhysicianTitleBar" className={cn}>
      <div className="flex-none flex items-center">
        <Button
          kind="primary:ghost"
          shape="square"
          className={`hidden ${isMobileDeviceOrApp() ? '' : 'sm:inline'}`}
          size="xs"
          onClick={event => {
            if (toggleMenu) toggleMenu();
            trackEvent(event);
          }}
        >
          {closeIconShown ? (
            <IconClose className={isActive ? 'text-blue-600' : 'text-slate-700'} />
          ) : (
            <IconMenu className={isActive ? 'text-blue-600' : 'text-slate-700'} />
          )}
        </Button>
      </div>
      <div className="flex-none flex items-center">
        <Link to="/">
          <Brand size="8" className={`ml-0 ${isMobileDeviceOrApp() ? '' : 'sm:ml-8'}`} />
        </Link>
      </div>
      <div className="flex-grow" />
      {children}
      <div className="flex-none flex items-center ml-4">
        <ContextMenu>
          <ContextMenu.Menu closeButtonLabel={t('PhysicianTitleBar.cancel')}>
            <ContextMenu.Item onClick={onClickFaq}>
              <IconQuestion size="5" />
              <div className="ml-4">{t('PhysicianTitleBar.faq')}</div>
            </ContextMenu.Item>
            <ContextMenu.Item className="news-element" onClick={onClickNewFeatures}>
              <BellIcon className="w-5 h-5 fill-current" />
              <div className="ml-4">{t('PhysicianTitleBar.newFeatures')}</div>
              <Badge className="ml-2 news-badge hidden" kind="signal">
                {t('PhysicianTitleBar.badge')}
              </Badge>
            </ContextMenu.Item>
          </ContextMenu.Menu>
          <ContextMenu.Trigger>
            {({ triggerRef }) => (
              <button
                className="flex items-center text-slate-500 focus:outline-none"
                type="button"
                ref={triggerRef}
              >
                <IconQuestion size="5" />
              </button>
            )}
          </ContextMenu.Trigger>
        </ContextMenu>
      </div>
      <div className="flex-none flex items-center">
        <ContextMenu>
          <ContextMenu.Menu closeButtonLabel={t('PhysicianTitleBar.cancel')}>
            <ContextMenu.Group>
              <ContextMenu.Item onClick={onClickAppSettings}>
                <IconCog size="5" />
                <div className="ml-4">{t('PhysicianTitleBar.app_settings')}</div>
              </ContextMenu.Item>
            </ContextMenu.Group>
            <ContextMenu.Group>
              <ContextMenu.Item onClick={onClickConfig}>
                <IconUserSolidCircle size="5" />
                <div className="ml-4">{t('PhysicianTitleBar.config')}</div>
              </ContextMenu.Item>
            </ContextMenu.Group>
            <ContextMenu.Group>
              <ContextMenu.Item onClick={onLogout}>
                <IconStandBy size="5" />
                <div className="ml-4">{t('PhysicianTitleBar.logout')}</div>
              </ContextMenu.Item>
            </ContextMenu.Group>
          </ContextMenu.Menu>
          <ContextMenu.Trigger>
            {({ triggerRef }) => (
              <div
                ref={triggerRef}
                className="flex items-center cursor-pointer hover:text-blue-500"
              >
                <Avatar
                  className="ml-4"
                  initials={initials}
                  src={avatar}
                  alt={fullNameWithTitle}
                  size="8"
                  data-testid="top-right-avatar-menu"
                />
              </div>
            )}
          </ContextMenu.Trigger>
        </ContextMenu>
      </div>
    </div>
  );
};

PhysicianTitleBar.defaultProps = {
  onClickConfig: () => {},
  onLogout: () => {},
  onClickFaq: () => {},
  onClickNewFeatures: () => {},
  isActive: true,
  children: null,
};

PhysicianTitleBar.propTypes = {
  toggleMenu: PropTypes.func.isRequired,
  avatar: PropTypes.string,
  closeIconShown: PropTypes.bool,
  isActive: PropTypes.bool,
  onClickConfig: PropTypes.func,
  onClickAppSettings: PropTypes.func,
  onLogout: PropTypes.func,
  onClickFaq: PropTypes.func,
  onClickNewFeatures: PropTypes.func,
  fullNameWithTitle: PropTypes.string.isRequired,
  initials: PropTypes.string.isRequired,
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func,
    PropTypes.array,
    PropTypes.object,
  ]),
  children: PropTypes.node,
};

export default PhysicianTitleBar;
